import pythonApiClient from "./pythonBaseApi";

const camaApi = {
  /**
   * Obtener todas las camas
   * @param {number|null} habitacionId - ID de la habitación (opcional)
   * @param {number|null} estadoCamaId - ID del estado de cama (opcional)
   * @returns {Promise} Lista de camas
   */
  getCamas: (habitacionId = null, estadoCamaId = null) => {
    const params = {};
    if (habitacionId) params.habitacion_id = habitacionId;
    if (estadoCamaId) params.estado_cama_id = estadoCamaId;
    
    return pythonApiClient.get("/mantenimientos/camas", { params });
  },

  /**
   * Obtener camas con estadísticas
   * @param {number|null} habitacionId - ID de la habitación (opcional)
   * @param {number|null} estadoCamaId - ID del estado de cama (opcional)
   * @param {number|null} moduloId - ID del módulo (opcional)
   * @returns {Promise} Lista de camas con estadísticas
   */
  getCamasWithStats: (habitacionId = null, estadoCamaId = null, moduloId = null) => {
    const params = {};
    if (habitacionId) params.habitacion_id = habitacionId;
    if (estadoCamaId) params.estado_cama_id = estadoCamaId;
    if (moduloId) params.modulo_id = moduloId;
    
    return pythonApiClient.get("/mantenimientos/camas/stats", { params });
  },

  /**
   * Obtener cama por ID
   * @param {number} camaId - ID de la cama
   * @returns {Promise} Datos de la cama
   */
  getCamaById: (camaId) => {
    return pythonApiClient.get(`/mantenimientos/camas/${camaId}`);
  },

  getPerfiles: () => {
    return pythonApiClient.get("/mantenimientos/camas/perfiles");
  },

  sincronizarLogiflex: (proyectoId, camaId, hotel = "CAMPAMENTO") => {
    return pythonApiClient.post(
      `/procesos/hoteleria/homologacion-camas-logiflex/sincronizar/${camaId}`,
      {},
      {
        params: { hotel },
        headers: { "X-Proyecto-ID": proyectoId },
      }
    );
  },

  /**
   * Obtener camas por habitación
   * @param {number} habitacionId - ID de la habitación
   * @returns {Promise} Lista de camas de la habitación
   */
  getCamasByHabitacion: (habitacionId) => {
    return pythonApiClient.get(`/mantenimientos/camas/habitacion/${habitacionId}`);
  },

  /**
   * Obtener camas libres por habitación
   * @param {number} habitacionId - ID de la habitación
   * @returns {Promise} Lista de camas libres
   */
  getCamasLibresByHabitacion: (habitacionId) => {
    return pythonApiClient.get(`/mantenimientos/camas/habitacion/${habitacionId}/libres`);
  },

  /**
   * Obtener camas ocupadas por habitación
   * @param {number} habitacionId - ID de la habitación
   * @returns {Promise} Lista de camas ocupadas
   */
  getCamasOcupadasByHabitacion: (habitacionId) => {
    return pythonApiClient.get(`/mantenimientos/camas/habitacion/${habitacionId}/ocupadas`);
  },

  /**
   * Crear cama
   * @param {Object} camaData - Datos de la cama
   * @returns {Promise} Cama creada
   */
  createCama: (camaData) => {
    return pythonApiClient.post("/mantenimientos/camas", camaData);
  },

  /**
   * Actualizar cama
   * @param {number} camaId - ID de la cama
   * @param {Object} camaData - Datos actualizados
   * @returns {Promise} Cama actualizada
   */
  updateCama: (camaId, camaData) => {
    return pythonApiClient.put(`/mantenimientos/camas/${camaId}`, camaData);
  },

  cambiarEstadoCama: (camaId, activo) => {
    return pythonApiClient.patch(`/mantenimientos/camas/${camaId}/estado`, { activo });
  },

  validarInactivacionCama: (camaId) => {
    return pythonApiClient.get(`/mantenimientos/camas/${camaId}/validar-inactivacion`);
  },

  /**
   * Eliminar cama
   * @param {number} camaId - ID de la cama
   * @returns {Promise} Resultado de la eliminación
   */
  deleteCama: (camaId) => {
    return pythonApiClient.delete(`/mantenimientos/camas/${camaId}`);
  },

  /**
   * Buscar camas
   * @param {string} searchTerm - Término de búsqueda
   * @param {number|null} habitacionId - ID de la habitación (opcional)
   * @returns {Promise} Lista de camas encontradas
   */
  searchCamas: (searchTerm, habitacionId = null) => {
    const params = { q: searchTerm };
    if (habitacionId) params.habitacion_id = habitacionId;
    
    return pythonApiClient.get("/mantenimientos/camas/search/", { params });
  },

  /**
   * Validar archivo Excel de carga masiva de camas
   * @param {File} file - Archivo Excel con columnas: ZONA, MODULO, HABITACION, TIPO, CAMA
   * @param {number} proyectoId - ID del proyecto
   * @returns {Promise} Respuesta de validación
   */
  validateBulkUploadFile: (file, proyectoId) => {
    const formData = new FormData();
    formData.append("file", file);

    return pythonApiClient.post(
      "/procesos/hoteleria/carga-masiva/camas/validate",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          "X-Proyecto-ID": proyectoId,
        },
      }
    );
  },

  /**
   * Procesar carga masiva de camas desde archivo Excel
   * @param {File} file - Archivo Excel con columnas: ZONA, MODULO, HABITACION, TIPO, CAMA
   * @param {number} proyectoId - ID del proyecto
   * @returns {Promise} Resultado del procesamiento
   */
  bulkUpload: (file, proyectoId) => {
    const formData = new FormData();
    formData.append("file", file);

    return pythonApiClient.post(
      "/procesos/hoteleria/carga-masiva/camas",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          "X-Proyecto-ID": proyectoId,
        },
      }
    );
  },
};

export default camaApi;
